/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { FC } from 'react';
import { graphql } from 'gatsby';
import moment from 'moment';
import loadable from '@loadable/component';

import { BgImage } from "gbimage-bridge";
import { getImage } from 'gatsby-plugin-image';

const Tabs = loadable(() => import('./tabs'));

import { Flex, Box, Heading, Text } from 'rebass';
import MainContainer from '../../ui-components/container/container';
import { LinkProps } from '../../ui-components/link/link';
import Breadcrumb from '../../ui-components/breadcrumb/breadcrumb';
import Links from '../links/links';

import { processMarkdown } from '../../utils/utils.js';

// @ts-ignore
import Facebook from '../../assets/svgs/social-media/facebook.svg';
// @ts-ignore
import Insta from '../../assets/svgs/social-media/insta.svg';
// @ts-ignore
import Linkedin from '../../assets/svgs/social-media/linkedin.svg';
// @ts-ignore
import Twitter from '../../assets/svgs/social-media/twitter.svg';
// @ts-ignore
import Email from '../../assets/svgs/social-media/email.svg';

interface Props {
  className?: string;
  data: {
    id?: string;
    title: string;
    subtitle: {
      subtitle: string;
    };
    image: any;
    headingColor: 'White' | 'Black' | 'Fuschia';
    links?: LinkProps[];
  };
  titleAs?: 'h1' | 'h2';
  subtitleAs?: 'h1' | 'h2';
  css?: any;
  author?: string;
  sortByDate?: any;
  articleType: 
      'Blog Post'
    | 'News Article'
    | 'Press Release'
    | 'Podcast'
    | 'Video';
  slug: string;
  lessonPlan?: boolean;
  scratch?: boolean;
  metaTitle: string;
  pageType?: 'Watch this first' | 'At a glance' | 'Project Sequence' | 'Extended learning' | 'Coder resources' | 'Student page';
  hideTabs: boolean;
}

export const query = graphql`
  fragment FullScreenImageSection on ContentfulFullScreenImageSection {
    id
    title
    subtitle {
      subtitle
    }
    image {
      gatsbyImageData(layout: FULL_WIDTH)
      internal {
        type
      }
    }
    links {
      ...Links
    }
    headingColor
    internal {
      type
    }
  }
`;

const Share = (slug) => {
  return (
    <Box mt="25px">
      <a
        href={`https://www.facebook.com/sharer/sharer.php?${encodeURI(
          `https://bootuppd.org/${slug}`
        )}`}
        target="__blank"
        data-gtm-link-track={`website-article-social-share--facebook`}
      >
        <span className="screen-reader--only">Share onFacebook</span>
        <Facebook className="social-icons" />
      </a>
      <a
        href={`https://twitter.com/intent/tweet?${encodeURI(
          `https://bootuppd.org/${slug}`
        )}`}
        target="__blank"
        data-gtm-link-track={`website-article-social-share--twitter`}
      >
        <span className="screen-reader--only">Share on Twitter</span>
        <Twitter className="social-icons" />
      </a>
      <a
        href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURI(
          `https://bootuppd.org/${slug}`
        )}`}
        target="__blank"
        data-gtm-link-track={`website-article-social-share--linkedin`}
      >
        <span className="screen-reader--only">Share on LinkedIn</span>
        <Linkedin className="social-icons" />
      </a>
      <a
        href={`mailto:?&subject=BootUp%20Article&body=Check%20out%20this%20article:%20https://bootuppd.org/${slug}`}
        target="__blank"
        data-gtm-link-track={`website-article-social-share--email`}
      >
        <span className="screen-reader--only">Share via email</span>
        <Email className="social-icons" />
      </a>
    </Box>
  )
};

const FullImageFoundation: FC<Props & JSX.IntrinsicElements['div']> = ({
  className = '',
  titleAs = 'h1',
  subtitleAs = 'h2',
  data,
  author,
  sortByDate,
  articleType,
  lessonPlan,
  pageType,
  slug,
  metaTitle,
  hideTabs,
  scratch = false,
  ...rest
}) => {
  const { title, subtitle, image, headingColor, links } = data;

  let computedHeadingColor;
  let computedBodyColor;

  if (headingColor === 'White') {
    computedHeadingColor = 'background';
    computedBodyColor = 'background';
  } else if (headingColor === 'Black') {
    computedHeadingColor = 'text';
    computedBodyColor = 'text';
  } else {
    computedHeadingColor = 'purple';
    computedBodyColor = 'purple';
  }

  const bgImage = getImage(image);

  return (
    <BgImage image={bgImage} className="image__full-screen">
      <Flex height="500px" alignItems="center">
        <MainContainer margin="auto" >
          {articleType && (
            <Box
              bg="fuschia"
              sx={{ borderRadius: '20px', fontFamily: 'body' }}
              color="white"
              p="10px 20px"
            >
              {articleType}
            </Box>
          )}
          <Box width="100%">
            <Heading as="h1" variant="heading" color={computedHeadingColor}>
              {title}
            </Heading>
          </Box>
          <Box width="100%" sx={{ color: computedBodyColor }}>
            {sortByDate && (
              <Heading as="p" variant="caption" mb={3}>
                {moment(sortByDate).format('MMMM D YYYY')} · By {author}
              </Heading>
            )}
            {subtitle && subtitle.subtitle && (
              <Text
                dangerouslySetInnerHTML={{
                  __html: processMarkdown(subtitle.subtitle)
                }}
              />
            )}
          </Box>
          {links && (
            <Flex width="100%">
              <Links links={links} />
            </Flex>
          )}
          {articleType && (
            <Share slug={slug} />
          )}
          {lessonPlan && !hideTabs && pageType !== 'Student page' && (
            <Tabs scratch={scratch} />
          )}
        </MainContainer>
      </Flex>
    </BgImage>
  );
};

export default FullImageFoundation;
